import { PickInfoFragment } from '@/api/entries/query.generated';
import { useNotifyOnMarketForEventsSubscription } from '@/api/events/query.generated';
import { recordInvocation } from '@/feature/devsettings/utils/invocations-tracker';
import { useUserStore } from '@/hooks/use-user-store';

/**
 * Hook that sets up live updates for entry markets based on live event IDs.
 * It subscribes to market updates and triggers the provided onUpdate callback
 * whenever there is a change in the current value of any market associated with the entry.
 */
export const useEntryLiveUpdates = ({
    liveEventIds,
    entryMarkets,
    onUpdate,
}: {
    liveEventIds: string[];
    entryMarkets: PickInfoFragment[];
    onUpdate: () => void;
}) => {
    // Used to to profile internal users only
    const email = useUserStore(state => state.profile?.email);
    const shouldProfileInvocations = email?.endsWith('@betr.app');
    useNotifyOnMarketForEventsSubscription(
        {
            variables: { eventIds: liveEventIds },
            pause: liveEventIds.length === 0,
        },
        (_, next) => {
            if (shouldProfileInvocations) {
                recordInvocation(`fantasy-ws-${liveEventIds.join('_')}`);
            }
            const newMarkets = next?.notifyOnMarketForEvents;
            // Check if there are any updates in the current values of the markets
            if (newMarkets?.length && newMarkets.length > 0) {
                const currentValueUpdated = newMarkets.some(playerMarket =>
                    playerMarket.markets?.some(market => {
                        const prev = entryMarkets?.find(
                            entryMarket =>
                                entryMarket.player.id === playerMarket.playerId &&
                                entryMarket.projection.marketId === market.marketId
                        );
                        return prev && prev.currentValue !== market.currentValue;
                    })
                );

                // Trigger the onUpdate callback if any market's current value has changed
                if (currentValueUpdated) {
                    onUpdate();
                }
            }
            return next;
        }
    );
};
